import { useState, useEffect } from 'react'
import { Shield, Key, Loader2 } from 'lucide-react'
import { Logo } from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useUser } from '@/app/store/auth'
import { 
    EnterpriseWebAuthnAPI, 
    registerEnterprisePasskey,
    getWebAuthnErrorMessage,
    isWebAuthnSupported 
} from '@/lib/security/webauthn-enterprise'
import { useToast } from '@/hooks/use-toast'
import { useAuthStore } from '@/app/store/auth'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'

export function WebAuthnSetupPage() {
    const [isLoading, setIsLoading] = useState(false)
    const [deviceName, setDeviceName] = useState('')
    const [error, setError] = useState<string | null>(null)
    const user = useUser()
    const { toast } = useToast()
    const { setTokens, setUser: setAuthUser, clearMfa } = useAuthStore()
    const navigate = useNavigate()
    const api = new EnterpriseWebAuthnAPI()

    useEffect(() => {
        if (!isWebAuthnSupported()) {
            setError('Your browser does not support passkeys. Please use a modern browser.')
        }
    }, [])

    const handleRegisterPasskey = async () => {
        if (!isWebAuthnSupported()) {
            toast({
                title: 'WebAuthn Not Supported',
                description: 'Your browser does not support passkeys. Please use a modern browser.',
                variant: 'destructive',
            })
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            // Begin registration - include email for MFA setup flow
            const options = await api.beginRegistration(user?.email)

            // Register the passkey
            const { credential, deviceName: detectedDeviceName } = await registerEnterprisePasskey(
                options,
                deviceName || undefined
            )

            // Complete registration - include email for MFA setup flow
            const result = await api.completeRegistration(credential, deviceName || detectedDeviceName, user?.email)

            // After successful registration, we need to complete the login
            // The backend should return tokens after WebAuthn setup
            if (result.data?.accessToken && result.data?.refreshToken) {
                setTokens(result.data.accessToken, result.data.refreshToken, 15 * 60)
                if (result.data.user) {
                    setAuthUser(result.data.user)
                }
                clearMfa()
                navigate('/')
                toast({
                    title: 'Passkey Setup Complete',
                    description: 'Your passkey has been successfully registered and you are now logged in.',
                })
            } else {
                // If no tokens returned, we need to trigger login completion
                // For now, just show success and redirect to login
                toast({
                    title: 'Passkey Registered',
                    description: 'Your passkey has been successfully registered. Please log in again.',
                })
                clearMfa()
                navigate('/login')
            }
        } catch (error: any) {
            const errorMessage = getWebAuthnErrorMessage(error)
            setError(errorMessage)
            toast({
                title: 'Registration Failed',
                description: errorMessage,
                variant: 'destructive',
            })
        } finally {
            setIsLoading(false)
        }
    }

    if (!isWebAuthnSupported()) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-4">
                <div className="w-full max-w-md space-y-6">
                    <div className="text-center space-y-4">
                        <div className="flex justify-center">
                            <Logo className="h-16" />
                        </div>
                        <div className="space-y-2">
                            <h1 className="text-2xl font-bold">Browser Not Supported</h1>
                            <p className="text-muted-foreground">
                                Your browser does not support passkeys. Please use a modern browser like Chrome, Firefox, Safari, or Edge.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6">
                {/* Header */}
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <Logo className="h-16" />
                    </div>
                    <div className="space-y-2">
                        <h1 className="text-2xl font-bold">Set Up Passkey</h1>
                        <p className="text-muted-foreground">
                            Register a security key or use your device's biometric authentication
                        </p>
                    </div>
                </div>

                {/* Setup form */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Shield className="h-5 w-5" />
                            Register Your Passkey
                        </CardTitle>
                        <CardDescription>
                            You can use a hardware security key (like YubiKey) or your device's built-in authenticator (Touch ID, Face ID, Windows Hello)
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Device Name Input */}
                        <div className="space-y-2">
                            <Label htmlFor="deviceName">Device Name (Optional)</Label>
                            <Input
                                id="deviceName"
                                placeholder="e.g., My YubiKey, Work Security Key, iPhone"
                                value={deviceName}
                                onChange={(e) => setDeviceName(e.target.value)}
                                disabled={isLoading}
                            />
                            <p className="text-xs text-muted-foreground">
                                Give your passkey a name to help you identify it later
                            </p>
                        </div>

                        {/* Error Display */}
                        {error && (
                            <Alert variant="destructive">
                                <Shield className="h-4 w-4" />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )}

                        {/* Instructions */}
                        <Alert>
                            <Key className="h-4 w-4" />
                            <AlertDescription>
                                <strong>How to set up your passkey:</strong>
                                <ul className="mt-2 space-y-1 text-sm list-disc list-inside">
                                    <li>Click the button below</li>
                                    <li>Follow your browser's prompts</li>
                                    <li>Use your security key or device biometrics</li>
                                    <li>You'll be logged in automatically after setup</li>
                                </ul>
                            </AlertDescription>
                        </Alert>

                        {/* Register Button */}
                        <Button
                            type="button"
                            className="w-full"
                            onClick={handleRegisterPasskey}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Registering Passkey...
                                </>
                            ) : (
                                <>
                                    <Key className="mr-2 h-4 w-4" />
                                    Register Passkey
                                </>
                            )}
                        </Button>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}

